// Runtime configuration (W1 — cutover prereq).
//
// Values that vary per environment (Supabase URL/anon key, backend API URLs) are loaded
// at startup from /config.json — written per-env by CI — instead of baked into the build.
// This lets ONE build artifact promote across environments unchanged.
//
// Local dev works with zero setup because public/config.json ships dev defaults; if that
// file is absent, we fall back to Vite build-time `VITE_*` env vars (e.g. a local .env).
//
// Load order matters: main.jsx calls loadRuntimeConfig() and only THEN dynamically imports
// the app, so any module that reads config at import time (e.g. src/supabase/supabase.js)
// evaluates after the config is in place.

const ENV_FALLBACK = {
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  VISUAL_ROUTER_API_URL: import.meta.env.VITE_VISUAL_ROUTER_API_URL,
  VISUAL_ROUTER_STREAM_URL: import.meta.env.VITE_VISUAL_ROUTER_STREAM_URL,
  // W2: path-based tenancy (/t/<slug>) vs legacy subdomain. Off until the router flip is proven.
  PATH_TENANCY: import.meta.env.VITE_PATH_TENANCY,

  // Stripe mode + prices are per-environment. They live here rather than being hardcoded in
  // PaymentStep.jsx because price IDs are MODE-SPECIFIC: a live price id 404s under a test key and
  // vice versa, so flipping the mode alone would break checkout. Onboarding previously sent
  // test_mode:false with live-only price ids baked into the bundle, which meant a signup on ANY
  // environment charged a real card.
  //
  // Defaults are LIVE, so an environment that sets nothing keeps the previous behaviour.
  STRIPE_TEST_MODE: import.meta.env.VITE_STRIPE_TEST_MODE,
  STRIPE_PRICE_SETUP: import.meta.env.VITE_STRIPE_PRICE_SETUP,
  STRIPE_PRICE_MONTHLY: import.meta.env.VITE_STRIPE_PRICE_MONTHLY,
  STRIPE_PRICE_SEAT: import.meta.env.VITE_STRIPE_PRICE_SEAT,
};

// Live-mode fallbacks: used when config.json omits the price ids. Keeping them here means an
// environment with no Stripe config behaves exactly as before this change.
export const STRIPE_LIVE_PRICES = {
  setup: 'price_1TK4UuLzASVKD57fznIFuD2v',   // $10,300 one-time
  monthly: 'price_1TK4UuLzASVKD57fktFCaPwg', // $1,030/mo
  seat: 'price_1TK5kILzASVKD57fxsDpx7Nd',    // $10.30/seat/mo
};

// Resolve the Stripe checkout settings for THIS environment.
//   testMode -> sent as test_mode, selecting sk_test_ vs sk_live_ on the API
//   prices   -> must match that mode; mismatched ids 404 at Stripe
export function getStripeCheckout() {
  const c = getConfig();
  const testMode = c.STRIPE_TEST_MODE === true || c.STRIPE_TEST_MODE === 'true';
  return {
    testMode,
    prices: {
      setup: c.STRIPE_PRICE_SETUP || STRIPE_LIVE_PRICES.setup,
      monthly: c.STRIPE_PRICE_MONTHLY || STRIPE_LIVE_PRICES.monthly,
      seat: c.STRIPE_PRICE_SEAT || STRIPE_LIVE_PRICES.seat,
    },
  };
}

let CONFIG = null;

function stripEmpty(obj) {
  const out = {};
  for (const k in obj) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') out[k] = obj[k];
  }
  return out;
}

// Fetch /config.json (no-store) and merge over the env fallback. Never throws — a missing
// config.json simply means "use the build-time env" (local dev).
export async function loadRuntimeConfig() {
  let fetched = {};
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok) fetched = await res.json();
  } catch {
    // no /config.json served (e.g. local dev without the file) — fall back to env
  }
  CONFIG = { ...ENV_FALLBACK, ...stripEmpty(fetched) };
  if (typeof window !== 'undefined') window.__CONFIG__ = CONFIG;
  return CONFIG;
}

// Synchronous accessor for modules that read config at import/use time. Falls back to a
// previously-set window.__CONFIG__ or the env, so callers never receive null (e.g. tests
// or code paths that run before loadRuntimeConfig()).
export function getConfig() {
  if (CONFIG) return CONFIG;
  if (typeof window !== 'undefined' && window.__CONFIG__) return window.__CONFIG__;
  return { ...ENV_FALLBACK };
}
